import { RestServiceConnection } from "../RestServiceConnection";

export interface ApiOption {
    id: string | number;
    name: string;
    connectId?: number | null;
    connectName?: string | null;
    projectCode?: string | null;
    medicalAffairs?: boolean;
    type?: string | null;
    subTypes?: string | null;
    publishedAt?: any;
}

export interface EngagementMetrics {
    total: number;
    mean: number;
    median: number;
    maximum: number;
    minimum: number;
    count: number;
}

export interface SubFormatResult {
    title: string;
    value: string;
    data: EngagementMetrics;
}

export interface FormatResult {
    title: string;
    data: EngagementMetrics;
    subFormats: SubFormatResult[];
}

export interface FormData {
    format: string;
    programme: string;
    programmeType: string;
    supporters: string[];
    comparisonOption: string;
}

const FORMAT_DISPLAY_NAMES: Record<string, string> = {
    'conference-update': 'Conference update',
    'newsletter': 'Speed Read',
    'podcast': 'Video podcast',
    'eks': 'EKS / On-demand webinar',
    'ipcv': 'Interactive Patient Case Video (IPCV)',
    'animated-video': 'Animated Video',
    'expert-video-animation': 'Animated Video',
    'masterclass': 'Masterclass / Symposium',
    'symposium': 'Masterclass / Symposium',
    'tweetorial': 'Tweetorial',
    'blueprint': 'Blueprint',
    'publication': 'Scientific Publication',
    'survey-campaign': 'Scientific Publication',
    'poster': 'Scientific Publication',
    'micro-learning': 'Micro learning - Video, slides and more',
    'e-learning': 'Interactive e-learning',
    'patient-support': 'Patient Support'
};

const EMPTY_METRICS: EngagementMetrics = {
    total: 0,
    mean: 0,
    median: 0,
    maximum: 0,
    minimum: 0,
    count: 0
};

const parseSubTypes = (subTypes?: string | null): string[] => {
    if (!subTypes) {
        return [];
    }

    return subTypes
        .split('$')
        .map((value) => value.trim())
        .filter(Boolean);
};

const formatSubTypeName = (value: string): string => {
    return value
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

export class TenDashboardService extends RestServiceConnection {

    getFormats = async (): Promise<ApiOption[]> => {
        try {
            const programmes = await this.getProgrammes();

            const types = new Set<string>();

            programmes.forEach((programme) => {
                if (
                    programme.type &&
                    programme.type.trim() !== ''
                ) {
                    types.add(programme.type);
                }
            });

            return Array.from(types)
                .sort()
                .map((type) => ({
                    id: type,
                    name: type
                }));
        } catch (error) {
            return [];
        }
    }

    getProgrammes = async (): Promise<ApiOption[]> => {
        try {
            this.response = await this.makeRequest({
                method: "post",
                url: '/programme/search',
                data: {}
            }, true);

            const programmes = this.getOnlyData() || [];

            const minimumPublishedDate = new Date('2023-01-01T00:00:00');

            const currentDate = new Date();

            const maximumPublishedDate = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth() - 6,
                currentDate.getDate(),
                23,
                59,
                59,
                999
            );

            return programmes
                .filter((programme: any) => {
                    const publishedDateValue =
                        programme.publishedAt?.date ??
                        programme.publishedAt ??
                        null;

                    if (!publishedDateValue) {
                        return false;
                    }

                    const publishedDate = new Date(
                        String(publishedDateValue)
                            .replace(' ', 'T')
                    );

                    return (
                        !Number.isNaN(publishedDate.getTime()) &&
                        publishedDate >= minimumPublishedDate &&
                        publishedDate <= maximumPublishedDate
                    );
                })
                .map((programme: any) => ({
                    id: programme.id,
                    name: programme.name,

                    connectId:
                        programme.connect?.id ??
                        programme.connectId ??
                        null,

                    connectName:
                        programme.connect?.name ??
                        programme.connectName ??
                        null,

                    projectCode:
                        programme.projectCode ??
                        null,

                    type:
                        programme.type ??
                        null,

                    subTypes:
                        programme.subTypes ??
                        null,

                    publishedAt:
                        programme.publishedAt ??
                        null,

                    medicalAffairs: Boolean(
                        programme.medicalAffairs ??
                        programme.medical_affairs ??
                        false
                    )
                }));
        } catch (error) {
            return [];
        }
    }

    getSupporters = async () => {
        try {
            this.response = await this.makeRequest({
                method: "post",
                url: '/partnership/search',
                data: {}
            }, true);

            const supporters = this.getOnlyData() || [];

            return supporters
                .filter(
                    (supporter: any) =>
                        supporter.type === 'supported-program'
                )
                .map((supporter: any) => ({
                    id: supporter.id,
                    name: supporter.name
                }));
        } catch (error) {
            return [];
        }
    }

    getTotalEngagement = async (
        filters: any
    ): Promise<FormatResult[] | null> => {
        try {
            const programmes = await this.getProgrammes();

            const requestedTypes: string[] =
                Array.isArray(filters.programme?.type)
                    ? filters.programme.type.map(String)
                    : [];

            const selectedProgrammes =
                Array.isArray(filters.programmes)
                    ? filters.programmes
                    : [];

            const selectedMedicalAffairs =
                typeof filters.programme?.medicalAffairs === 'boolean';

            const selectedBrand =
                filters.selectedBrand ||
                (
                    selectedMedicalAffairs
                        ? (
                            filters.programme.medicalAffairs
                                ? 'medAffairs'
                                : 'cor2ed'
                        )
                        : ''
                );

            const baseFilters = {
                between_dates: filters.between_dates,

                programmePublishedFrom: '2023-01-01',

                ...(
                    Array.isArray(filters.supporters) &&
                    filters.supporters.length > 0
                        ? {
                            supporters: filters.supporters
                        }
                        : {}
                )
            };

            const getCompanyProgrammeFilter = (): {
                medicalAffairs?: boolean;
            } => {
                if (selectedBrand === 'medAffairs') {
                    return {
                        medicalAffairs: true
                    };
                }

                if (selectedBrand === 'cor2ed') {
                    return {
                        medicalAffairs: false
                    };
                }

                return {};
            };

            const getValidProgrammeIds = (types: string[] = [], subType?: string): number[] => {
                return programmes
                    .filter((programme) => {
                        // Filtrar por Format
                        if (
                            types.length > 0 &&
                            !types.includes(String(programme.type))
                        ) {
                            return false;
                        }

                        // Filtrar por Sub-Format
                        if (
                            subType &&
                            !parseSubTypes(programme.subTypes)
                                .includes(subType)
                        ) {
                            return false;
                        }

                        // Respetar Programme seleccionado
                        if (
                            selectedProgrammes.length > 0 &&
                            !selectedProgrammes
                                .map(String)
                                .includes(String(programme.id))
                        ) {
                            return false;
                        }

                        // Medical Affairs
                        if (
                            selectedBrand === 'medAffairs' &&
                            programme.medicalAffairs !== true
                        ) {
                            return false;
                        }

                        // COR2ED
                        if (
                            selectedBrand === 'cor2ed' &&
                            programme.medicalAffairs !== false
                        ) {
                            return false;
                        }

                        return true;
                    })
                    .map((programme) => Number(programme.id))
                    .filter((id) => !Number.isNaN(id));
            };

            const getSubTypesForFormats = (
                types: string[]
            ): Array<{
                value: string;
                count: number;
            }> => {
                const subTypeProgrammes = new Map<string, Set<string>>();

                programmes
                    .filter((programme) => {
                        if (!types.includes(String(programme.type))) {
                            return false;
                        }

                        if (
                            selectedProgrammes.length > 0 &&
                            !selectedProgrammes
                                .map(String)
                                .includes(String(programme.id))
                        ) {
                            return false;
                        }

                        if (
                            selectedBrand === 'medAffairs' &&
                            programme.medicalAffairs !== true
                        ) {
                            return false;
                        }

                        if (
                            selectedBrand === 'cor2ed' &&
                            programme.medicalAffairs !== false
                        ) {
                            return false;
                        }

                        return true;
                    })
                    .forEach((programme) => {
                        parseSubTypes(programme.subTypes)
                            .forEach((subType) => {
                                if (!subTypeProgrammes.has(subType)) {
                                    subTypeProgrammes.set(
                                        subType,
                                        new Set<string>()
                                    );
                                }

                                subTypeProgrammes
                                    .get(subType)!
                                    .add(String(programme.id));
                            });
                    });

                return Array.from(subTypeProgrammes.entries())
                    .map(([value, programmeIds]) => ({
                        value,
                        count: programmeIds.size
                    }))
                    .sort((a, b) =>
                        a.value.localeCompare(b.value)
                    );
            };

            const makeRequestPayload = async (
                payloadFilters: any
            ): Promise<EngagementMetrics> => {
                const programmeFilter: Record<string, any> = {};

                if (
                    Array.isArray(payloadFilters.programme?.type) &&
                    payloadFilters.programme.type.length > 0
                ) {
                    programmeFilter.type =
                        payloadFilters.programme.type;
                }

                if (payloadFilters.programme?.subType) {
                    programmeFilter.subType =
                        payloadFilters.programme.subType;
                }

                if (
                    typeof payloadFilters.programme?.medicalAffairs ===
                    'boolean'
                ) {
                    programmeFilter.medicalAffairs =
                        payloadFilters.programme.medicalAffairs;
                }

                this.response = await this.makeRequest({
                    method: "post",
                    url: '/metric-stored/total-engagement/stats',
                    data: {
                        filter_filters: {
                            between_dates:
                                payloadFilters.between_dates,

                            ...(
                                payloadFilters.programmePublishedFrom
                                    ? {
                                        programme_published_from:
                                            payloadFilters.programmePublishedFrom
                                    }
                                    : {}
                            ),

                            ...(
                                Object.keys(programmeFilter).length > 0
                                    ? {
                                        programme: programmeFilter
                                    }
                                    : {}
                            ),

                            ...(
                                Array.isArray(
                                    payloadFilters.programmes
                                ) &&
                                payloadFilters.programmes.length > 0
                                    ? {
                                        programmes:
                                            payloadFilters.programmes
                                    }
                                    : {}
                            ),

                            ...(
                                Array.isArray(
                                    payloadFilters.supporters
                                ) &&
                                payloadFilters.supporters.length > 0
                                    ? {
                                        supporters:
                                            payloadFilters.supporters
                                    }
                                    : {}
                            )
                        }
                    }
                }, true);

                const data =
                    this.getOnlyData() ||
                    this.response?.data;

                return {
                    total: Number(data?.total ?? 0),
                    mean: Number(data?.mean ?? 0),
                    median: Number(data?.median ?? 0),
                    maximum: Number(data?.maximum ?? 0),
                    minimum: Number(data?.minimum ?? 0),
                    count: Number(data?.count ?? 0)
                };
            };

            const getSubFormatResults = async (
                formatTypes: string[]
            ): Promise<SubFormatResult[]> => {
                const subTypes =
                    getSubTypesForFormats(formatTypes);

                const subFormatResults: SubFormatResult[] = [];

                for (const subTypeInfo of subTypes) {
                    try {
                        const validProgrammeIds =
                            getValidProgrammeIds(
                                formatTypes,
                                subTypeInfo.value
                            );

                        if (validProgrammeIds.length === 0) {
                            continue;
                        }

                        const data = await makeRequestPayload({
                            ...baseFilters,

                            programme: {
                                type: formatTypes,
                                subType: subTypeInfo.value,
                                ...getCompanyProgrammeFilter()
                            },

                            programmes: validProgrammeIds
                        });

                        if (data.count > 0) {
                            subFormatResults.push({
                                title: formatSubTypeName(
                                    subTypeInfo.value
                                ),

                                value: subTypeInfo.value,

                                data
                            });
                        }

                    } catch (error) {
                        continue;
                    }
                }

                return subFormatResults;
            };

            const results: FormatResult[] = [];

            const pushResult = async (
                title: string,
                formatTypes: string[],
                payloadFilters: any
            ) => {
                try {
                    if (
                        Array.isArray(payloadFilters.programmes) &&
                        payloadFilters.programmes.length === 0
                    ) {
                        return;
                    }

                    const data =
                        await makeRequestPayload(payloadFilters);

                    const subFormats =
                        formatTypes.length > 0
                            ? await getSubFormatResults(formatTypes)
                            : [];

                    results.push({
                        title,
                        data,
                        subFormats
                    });

                } catch (error) {
                    results.push({
                        title,
                        data: {
                            ...EMPTY_METRICS
                        },
                        subFormats: []
                    });
                }
            };

            if (requestedTypes.length > 0) {
                const groupedFormats:
                    Record<string, string[]> = {};

                for (const formatType of requestedTypes) {
                    const type = String(formatType);

                    const displayName =
                        FORMAT_DISPLAY_NAMES[type] ||
                        type;

                    if (!groupedFormats[displayName]) {
                        groupedFormats[displayName] = [];
                    }

                    groupedFormats[displayName].push(type);
                }

                for (
                    const [displayName, types]
                    of Object.entries(groupedFormats)
                ) {
                    await pushResult(
                        displayName,
                        types,
                        {
                            ...baseFilters,

                            programme: {
                                type: types,
                                ...getCompanyProgrammeFilter()
                            },

                            programmes: getValidProgrammeIds(types)
                        }
                    );
                }
            } else if (
                (!selectedBrand || selectedBrand === 'both') &&
                selectedProgrammes.length === 0
            ) {
                const allFormats =
                    await this.getFormats();

                const groupedFormats:
                    Record<string, string[]> = {};

                for (const format of allFormats) {
                    const type =
                        String(format.id);

                    const displayName =
                        FORMAT_DISPLAY_NAMES[type] ||
                        format.name;

                    if (!groupedFormats[displayName]) {
                        groupedFormats[displayName] = [];
                    }

                    groupedFormats[displayName].push(type);
                }

                for (
                    const [displayName, types]
                    of Object.entries(groupedFormats)
                ) {
                    await pushResult(
                        displayName,
                        types,
                        {
                            ...baseFilters,

                            programme: {
                                type: types,
                                ...getCompanyProgrammeFilter()
                            },

                            programmes: getValidProgrammeIds(types)
                        }
                    );
                }
            }

            if (selectedProgrammes.length > 0) {
                const programmeLabel =
                    filters.selectedProgrammeLabel ||
                    (
                        selectedProgrammes.length === 1
                            ? 'Programme'
                            : 'Selection'
                    );

                await pushResult(
                    programmeLabel,
                    [],
                    {
                        ...baseFilters,
                        programmes: getValidProgrammeIds()
                    }
                );
            }

            if (
                selectedBrand === 'cor2ed' ||
                selectedBrand === 'medAffairs'
            ) {
                const brandLabel =
                    selectedBrand === 'medAffairs'
                        ? 'Medical Affairs'
                        : 'COR2ED';

                await pushResult(
                    brandLabel,
                    [],
                    {
                        ...baseFilters,

                        programme: {
                            ...getCompanyProgrammeFilter()
                        },

                        programmes: getValidProgrammeIds()
                    }
                );
            }

            return results;

        } catch (error) {
            return null;
        }
    }
}